import React, { useCallback } from 'react';
import { useFormContext } from 'react-hook-form';
import { isBefore } from 'date-fns';

import {
  guessTimezone,
  convertToTimezone,
  parseUTCDate,
  formatDateInTimezone
} from 'utils/dateTimeUtils';
import { DateTimePickerField } from '@epoch-ui/components/Form';
import { Button } from '@epoch-ui/components/Button';
import { useEditableEvent } from '@event-editor/hooks/useEditableEvent';
import { Icon } from '@epoch-ui/components/Icon';
import { TimeOffsetFieldV2 } from '@epoch-components/TimeOffsetField/TimeOffsetFieldV2';
import { TimeOffsetConfigPresets } from '@epoch-components/TimeOffsetField/presets';
import { PRETTY_DATE_TIME_TZ_FORMAT } from 'constants/dates';

import { getPostEventReminderWindowEnd } from '../../utils/postEventReminderWindow';

export const ReminderScheduler: React.FC = () => {
  const event = useEditableEvent();
  const { watch, reset } = useFormContext();

  const schedule = watch('schedule');
  const isRelativeTime = schedule?.isRelativeTime ?? true;
  // Past events are absolute-only for *new* choices, but a reminder already saved in
  // relative mode needs an escape hatch to absolute — otherwise it's stranded once its
  // event ends. Its an edge case but can happen.
  const canToggleScheduleMode = !event.hasEnded || isRelativeTime;

  const eventStartDate = event.datetimeScheduled
    ? parseUTCDate(event.datetimeScheduled)
    : new Date();

  const isEventStartsInThePast = isBefore(eventStartDate, new Date());

  const getCalendarDateDisabled = useCallback(
    (date: Date): boolean => {
      const targetTz = event.timezoneCode ?? guessTimezone();
      const today = new Date();
      const todayInTargetTz = convertToTimezone(today, targetTz);
      const dateInTargetTz = convertToTimezone(date, targetTz);

      // Compare dates only (ignore time)
      const todayDateOnly = formatDateInTimezone(todayInTargetTz, targetTz, 'YYYY-MM-DD');
      const dateOnly = formatDateInTimezone(dateInTargetTz, targetTz, 'YYYY-MM-DD');
      const windowEnd = getPostEventReminderWindowEnd(event.datetimeEnd);
      const windowEndDateOnly = windowEnd
        ? formatDateInTimezone(windowEnd.toDate(), targetTz, 'YYYY-MM-DD')
        : null;

      return dateOnly < todayDateOnly || (!!windowEndDateOnly && dateOnly > windowEndDateOnly);
    },
    [event.datetimeEnd, event.timezoneCode]
  );

  // On switching between relative/absolute, reset fields accordingly
  const handleToggle = useCallback(() => {
    // Switch to absolute time
    if (isRelativeTime) {
      reset((currentValues) => ({
        ...currentValues,
        schedule: {
          ...currentValues.schedule,
          isRelativeTime: false
        }
      }));
    } else {
      // Switch to relative time
      reset((currentValues) => ({
        ...currentValues,
        schedule: {
          ...currentValues.schedule,
          isRelativeTime: true
        }
      }));
    }
  }, [isRelativeTime, reset]);

  return (
    <div className="tw-mt-4">
      <h3 className="tw-mb-0 tw-text-sm tw-font-medium">When should the reminder go out?</h3>

      <p className="tw-mb-0 tw-text-xs tw-leading-5 tw-text-primary-grey">
        Event is scheduled for{' '}
        {formatDateInTimezone(
          parseUTCDate(event.datetimeScheduled!),
          event.timezoneCode!,
          `${PRETTY_DATE_TIME_TZ_FORMAT} z`
        )}
      </p>

      {/* Informational message to show if event start time is in the past */}
      {isEventStartsInThePast && (
        <p className="tw-mt-0 tw-flex tw-items-center tw-text-xs tw-leading-5 tw-text-yellow ">
          <Icon name="info" size={16} className="tw-mr-1" />
          Event start time is in the past
        </p>
      )}

      {isRelativeTime ? (
        // Relative time scheduler
        <div className="tw-mt-3">
          <TimeOffsetFieldV2 name="schedule" config={TimeOffsetConfigPresets.eventBegins}>
            <TimeOffsetFieldV2.Quantity className="tw-w-20" min={1} />
            <TimeOffsetFieldV2.Unit className="tw-w-40" />
            <TimeOffsetFieldV2.Relation />
          </TimeOffsetFieldV2>
        </div>
      ) : (
        // Absolute time scheduler
        <div className="tw-mt-3">
          <DateTimePickerField name="schedule.sendAt" disabled={isRelativeTime}>
            <DateTimePickerField.DateInput placeholder="Date" disabled={getCalendarDateDisabled} />
            <DateTimePickerField.Time interval={5} placeholder="Time" />
          </DateTimePickerField>
        </div>
      )}

      {/* Button to toggle between relative and absolute time scheduler */}
      {canToggleScheduleMode && (
        <Button
          variant="link"
          className="tw-mt-3 tw-h-5 tw-p-0 tw-font-normal"
          type="button"
          onClick={() => {
            handleToggle();
          }}
          disabled={false}
        >
          {isRelativeTime ? 'Use date and time' : 'Use relative time'}
        </Button>
      )}
    </div>
  );
};
